import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { flushSync } from "react-dom";
import { formatCents } from "@shared/money";
import { formatGuestCount, leadGuests } from "@shared/guestCount";
import { leadConvert } from "@shared/schemas/leads";
import { errorMessage } from "@web/lib/api";
import { validateLead } from "@shared/validation/leads";
import { blockingIssues, evaluation, parseInput } from "@shared/validation/issues";
import { fmtDate } from "@web/lib/format";
import { useFormValidation } from "@web/lib/formValidation";
import { catalogQuery } from "@web/lib/queries";
import { leadApi, type LeadDetail } from "@web/lib/queries-leads";
import { BottomSheet, Button, Picker, Stack, Text, UnsavedChanges, ValidationSummary, useToast } from "@web/ui";

/** Create booking from a lead (SPEC §7.3): package from the venue's catalog for the event date → POST /api/leads/:id/convert → the booking. */
export function ConvertSheet({ lead, onClose }: { lead: LeadDetail; onClose: () => void }) {
  const navigate = useNavigate();
  const toast = useToast();
  const local = validateLead({ ...lead, contact: undefined, secondaryContact: undefined }, { action: "lead.convert", primaryContact: lead.contact, secondaryContact: lead.secondaryContact });
  const ready = blockingIssues(local, "lead.convert").length === 0;
  const guests = leadGuests(lead);
  const catalog = useQuery({ ...catalogQuery({ venueId: lead.venueId, date: lead.eventDate }), enabled: ready });
  const packages = useMemo(() => (catalog.data ?? []).filter((c) => c.kind === "package" && !c.pricingAction), [catalog.data]);
  const [packageId, setPackageId] = useState("");
  const [failure, setFailure] = useState<string | null>(null);
  const parsed = parseInput(leadConvert, packageId ? { packageCatalogItemId: packageId } : {}, { type: "lead", id: lead.id }, "lead.convert");
  const validation = useFormValidation(evaluation([...parsed.issues, ...local.issues], false));
  const { busy } = validation;

  async function convert() {
    if (!parsed.success || !validation.begin("lead.convert")) return;
    setFailure(null);
    try {
      const r = await leadApi.convert(lead.id, parsed.data);
      toast.success("Booking created");
      // Remove this successfully saved draft's navigation guard before routing.
      flushSync(onClose);
      navigate({ to: "/bookings/$id", params: { id: r.booking.id } });
    } catch (e) {
      validation.reject(e); setFailure(errorMessage(e));
    } finally {
      validation.finish();
    }
  }

  return (
    <BottomSheet
      open
      onClose={() => validation.edit(onClose)}
      title="Create booking"
      footer={
        <Button loading={busy} disabled={!ready} onClick={convert}>
          Create booking
        </Button>
      }
    >
      <Stack gap={3}>
        <UnsavedChanges dirty={Boolean(packageId)} pending={busy} />
        <ValidationSummary result={validation.result} local unavailable={validation.unavailable} />
        {failure && <div role="alert"><Text tone="danger">{failure}</Text></div>}
        <Text variant="callout" tone={ready ? "secondary" : "danger"}>
          {ready ? `${lead.venue?.name} · ${fmtDate(lead.eventDate, { weekday: true })}${guests ? ` · ${formatGuestCount(guests.min, guests.max)} guests` : ""}. The lead becomes Converted and links to the booking.` : "Add a venue and an event date in Details first."}
        </Text>
        <Picker
          label="Package"
          placeholder={!ready ? "Needs a venue and date" : packages.length ? "No package yet (add lines on the booking)" : "No packages for this venue"}
          value={packageId}
          onChange={value => validation.edit(() => { setPackageId(value); setFailure(null); })}
          {...validation.field("packageCatalogItemId")}
          disabled={busy || !ready}
          options={packages.map((p) => ({ value: p.id, label: `${p.name} — ${formatCents(p.resolvedAmount)}${p.pricing === "hourly" ? "/hr" : p.pricing === "per_guest" ? "/guest" : ""}` }))}
          hint="The venue's default fee lines and the processing fee are added automatically."
        />
      </Stack>
    </BottomSheet>
  );
}
